import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';

const host = getConnectorHost('claude');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'connect-claude', title: '3. Connect in Claude' },
  { id: 'connect-claude-code', title: '4. Connect in Claude Code' },
  { id: 'approve', title: '5. Approve in Chat.co' },
  { id: 'first-answer', title: '6. Your first cited answer' },
  { id: 'credits', title: '7. Message credits' },
  { id: 'troubleshooting', title: '8. Common errors' },
  { id: 'disconnect', title: '9. Disconnect' },
  { id: 'developer-reference', title: '10. For developers & agents' },
  { id: 'references', title: '11. References' },
];

const CLAUDE_CODE_ADD = `claude mcp add --transport http chatco ${CHATCO_MCP_URL}`;
const CLAUDE_CODE_ADD_USER = `claude mcp add --transport http --scope user chatco ${CHATCO_MCP_URL}`;

const ClaudeConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in Claude"
      description="Connect Chat.co to Claude and Claude Code: sign in with OAuth, choose a workspace, and get cited answers from your own Chat.co experts."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in Claude | Chat.co Docs',
        description:
          'Connect Chat.co to Claude and Claude Code and get cited answers from your own experts.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['claude', 'claude code', 'connector', 'mcp', 'experts', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in Claude"
      description="Add the published Chat.co connector to Claude, or the same server to Claude Code, and ask your experts for cited answers."
      readingTime="8 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('claude')}
      keyTakeaways={[
        'Chat.co is published in Claude’s connector directory.',
        'Claude Code adds the same server with one command and signs in through /mcp.',
        'You sign in to your own Chat.co account and choose one workspace; no API key is involved.',
        'Answers cite their sources, and sources open only with your current Chat.co access.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          The directory listing covers Claude on the web, Claude Desktop, the Claude mobile apps,
          Claude Code and the Claude API. We ran the full flow in Claude on the web: listing
          experts, a cited answer, a follow-up, a direct search and opening a source. The other
          surfaces use the same connector, but we have not tested each of them separately.
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>A Claude account.</strong> Anthropic offers connectors on Free, Pro, Max, Team
            and Enterprise plans. On Team and Enterprise, your organization&apos;s settings may
            require an Owner to add or allow a connector before members can use it.
          </li>
          <li>
            <strong>For Claude Code:</strong> Claude Code installed and signed in.
          </li>
        </ConnectorPrerequisites>
      </section>

      <section id="connect-claude" className="scroll-mt-28">
        <h2>3. Connect in Claude (web, Desktop, mobile)</h2>
        <DocsSteps>
          <DocsStep title="Open the Chat.co listing">
            <p>
              Go to{' '}
              <a
                href="https://claude.ai/directory/chatco"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary-700 underline">
                claude.ai/directory/chatco
              </a>
              . Or, in Claude, open <strong>Customize → Connectors</strong>, select{' '}
              <strong>+</strong> and browse for <strong>Chat.co</strong>.
            </p>
          </DocsStep>
          <DocsStep title="Select Connect">
            <p>Claude opens the Chat.co sign-in page.</p>
          </DocsStep>
          <DocsStep title="Sign in and approve in Chat.co">
            <p>
              Choose the workspace and approve the connection, as described in{' '}
              <a href="#approve" className="font-medium text-primary-700 underline">
                Approve in Chat.co
              </a>
              . You return to Claude afterwards.
            </p>
          </DocsStep>
          <DocsStep title="Turn Chat.co on in a conversation">
            <p>
              In a chat, select <strong>+</strong> in the lower left (or type <code>/</code>), open{' '}
              <strong>Connectors</strong> and switch <strong>Chat.co</strong> on.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="note" title="Mobile">
          Connectors added on the web or in Claude Desktop are available in the Claude mobile apps.
          Anthropic describes installing connectors on mobile as beta, so add Chat.co on the web or
          in Desktop first.
        </DocsCallout>
      </section>

      <section id="connect-claude-code" className="scroll-mt-28">
        <h2>4. Connect in Claude Code</h2>
        <p>
          Claude Code adds remote servers from the command line, as documented by Anthropic. Chat.co
          uses the HTTP transport.
        </p>
        <DocsSteps>
          <DocsStep title="Add the server">
            <DocsCodeBlock code={CLAUDE_CODE_ADD} language="bash" />
            <p>
              This adds it for the current project only. To use it in every project, add{' '}
              <code>--scope user</code>:
            </p>
            <DocsCodeBlock code={CLAUDE_CODE_ADD_USER} language="bash" />
          </DocsStep>
          <DocsStep title="Sign in">
            <p>
              Start Claude Code, run <code>/mcp</code>, choose <strong>chatco</strong> and
              authenticate. Your browser opens the Chat.co approval page.
            </p>
          </DocsStep>
          <DocsStep title="Check the tools">
            <p>
              After approval, <code>/mcp</code> shows chatco as connected, with four tools:{' '}
              <code>list_authorized_experts</code>, <code>consult_expert</code>, <code>search</code>{' '}
              and <code>fetch</code>.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="tip" title="Claude API">
          Developers using Anthropic&apos;s MCP connector in the Messages API must obtain the
          user&apos;s OAuth access token themselves and send it with the server URL. See
          Anthropic&apos;s MCP connector documentation under References. This guide doesn&apos;t
          cover building that flow.
        </DocsCallout>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>5. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>6. Your first cited answer</h2>
        <FirstRunWalkthrough where="a Claude conversation with Chat.co switched on, or in a Claude Code session" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>7. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>8. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'Claude answers without using Chat.co',
              'Switch Chat.co on for the conversation (+ → Connectors), or name it in the request: “Ask my Chat.co experts…”.',
            ],
            [
              'Chat.co is missing from Browse on a Team or Enterprise plan',
              'Ask an organization Owner to add or allow the connector.',
            ],
            [
              'Claude Code lists chatco but its tools fail',
              'Run /mcp, select chatco and authenticate again.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>9. Disconnect</h2>
        <ChatcoDisconnect>
          <p>
            In Claude, open <strong>Customize → Connectors</strong>, select Chat.co and disconnect
            it. In Claude Code, run:
          </p>
          <DocsCodeBlock code="claude mcp remove chatco" language="bash" />
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>10. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>11. References</h2>
        <ConnectorReferences
          vendorLinks={[
            {
              label: 'Chat.co in the Claude directory',
              href: 'https://claude.ai/directory/chatco',
            },
            {
              label: 'Anthropic: Use connectors to extend Claude’s capabilities',
              href: 'https://support.claude.com/en/articles/11176164-use-connectors-to-extend-claude-s-capabilities',
            },
            {
              label: 'Anthropic: Connect Claude Code to tools via MCP',
              href: 'https://code.claude.com/docs/en/mcp',
            },
            {
              label: 'Anthropic: MCP connector (Claude API)',
              href: 'https://platform.claude.com/docs/en/agents-and-tools/mcp-connector',
            },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default ClaudeConnectorGuide;
